import { useMemo, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Bar, Cell, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { UsersIcon } from "@heroicons/react/24/outline";
import { CheckIcon, ChevronDownIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { cn, formatAmount } from "@utils";
import {
  LAYER_COUNT,
  buildBurndown,
  burndownLayers,
  burndownMonthName,
  shortMonthName,
  useOverdueBurndown,
} from "../hooks/overdueBurndown";
import { DEFAULT_WORKING_SET_ICON, WORKING_SET_ICONS } from "../hooks/workingSets";

// One ramp per state, light → dark with age (index 0 = due this month): gray normally, violet on days Ontik
// sent payment requests.
const GRAY = ["#ededef", "#e4e4e7", "#d4d4d8", "#bdbdc4", "#a1a1aa"];
const VIOLET = ["#ede9fe", "#ddd6fe", "#c4b5fd", "#a78bfa", "#8b5cf6"];
const MARKER = "#6d28d9";
const LAST_MONTH = "#71717a";
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const compactAmount = (value) =>
  `£${Number(value).toLocaleString("en-GB", { notation: "compact", maximumFractionDigits: 0 }).toLowerCase()}`;

const percent = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—");

const setIcon = (set) => WORKING_SET_ICONS[set.icon] ?? WORKING_SET_ICONS[DEFAULT_WORKING_SET_ICON];

const PlaceholderBar = ({ x, y, width, height }) =>
  height > 0 ? (
    <rect x={x + 0.5} y={y + 0.5} width={Math.max(width - 1, 0)} height={Math.max(height - 1, 0)} rx={3}
      fill="none" stroke="#d4d4d8" strokeDasharray="3 3" />
  ) : null;

const TouchpointMarker = ({ viewBox }) => (
  <path d={`M${viewBox.x - 5},${viewBox.y - 9} h10 l-5,7 z`} fill={MARKER} />
);

const ComparedToLastMonth = ({ row, today }) => {
  const change = row.total - row.lastMonth;
  if (change === 0) return null;
  const lastMonthLabel = `${row.day} ${shortMonthName(new Date(today.getFullYear(), today.getMonth() - 1, 1))}`;
  return (
    <div className="mt-2">
      <p className={cn("font-medium", change < 0 ? "text-green-400" : "text-red-400")}>
        {change < 0 ? "▼" : "▲"} {formatAmount(Math.abs(change))} ({percent(Math.abs(change), row.lastMonth)}){" "}
        {change < 0 ? "lower" : "higher"}
      </p>
      <p className="text-zinc-500">than {lastMonthLabel}, same point last month</p>
    </div>
  );
};

const MovementLine = ({ sign, label, amount, strong }) => (
  <div className={cn("flex justify-between gap-4", strong ? "font-semibold text-white" : "text-zinc-400")}>
    <dt>
      {sign && <span className="inline-block w-3">{sign}</span>}
      {label}
    </dt>
    <dd className="tabular-nums">{formatAmount(amount)}</dd>
  </div>
);

// How the day's close was reached from the day before. The parts are worked out per invoice, so they always
// sum to the close; "cleared" covers payments, credit notes and write-offs alike.
const DayMovement = ({ movement, closing }) => (
  <dl className="mt-2 space-y-0.5 border-t border-zinc-700 pt-2">
    <MovementLine label="Opening" amount={movement.opening} />
    {movement.fellDue > 0 && <MovementLine sign="+" label="Fell due" amount={movement.fellDue} />}
    {movement.cleared > 0 && <MovementLine sign="−" label="Cleared" amount={movement.cleared} />}
    {movement.otherIncrease > 0 && <MovementLine sign="+" label="Other increases" amount={movement.otherIncrease} />}
    <MovementLine sign="=" label="Closing" amount={closing} strong />
  </dl>
);

const BurndownTooltip = ({ active, payload, layers, today }) => {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  const date = new Date(today.getFullYear(), today.getMonth(), row.day);
  return (
    <div className="w-60 rounded-lg bg-zinc-900 px-3 py-2.5 text-xs text-zinc-300 shadow-lg">
      <div className="flex items-baseline justify-between">
        <span className="font-semibold text-white">
          {row.day} {shortMonthName(date)}
        </span>
        <span className="text-zinc-500">{WEEKDAYS[date.getDay()]}</span>
      </div>
      {row.total !== null ? (
        <>
          <p className="mt-1 text-lg font-semibold leading-tight text-white">{formatAmount(row.total)}</p>
          <p className="text-zinc-500">overdue</p>
          <ul className="mt-2 space-y-0.5">
            {[...layers].reverse().map((layer) =>
              row[layer.key] > 0 ? (
                <li key={layer.key} className="flex items-center gap-2">
                  <span className="size-2 rounded-sm" style={{ backgroundColor: GRAY[layer.index] }} />
                  <span className="flex-1">{layer.label}</span>
                  <span className="tabular-nums text-white">{formatAmount(row[layer.key])}</span>
                  <span className="w-8 text-right tabular-nums text-zinc-500">{percent(row[layer.key], row.total)}</span>
                </li>
              ) : null,
            )}
          </ul>
          {row.movement && <DayMovement movement={row.movement} closing={row.total} />}
          {row.collected > 0 && <p className="mt-1 text-zinc-500">{formatAmount(row.collected)} received in payments</p>}
          {row.lastMonth !== null && <ComparedToLastMonth row={row} today={today} />}
          {row.touchpoints > 0 && (
            <p className="mt-1 text-violet-300">
              {row.touchpoints} payment request{row.touchpoints === 1 ? "" : "s"} sent
            </p>
          )}
        </>
      ) : (
        <p className="mt-1 text-zinc-500">{row.future ? "Still to come" : "No data"}</p>
      )}
    </div>
  );
};

const Legend = ({ layers }) => (
  <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-zinc-600">
    {layers.map((layer) => (
      <li key={layer.key} className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-sm" style={{ backgroundColor: GRAY[layer.index] }} />
        {layer.label}
      </li>
    ))}
    <li className="flex items-center gap-1.5">
      <span className="h-0.5 w-4 rounded-full" style={{ backgroundColor: LAST_MONTH }} />
      Same day last month
    </li>
    <li className="flex items-center gap-1.5">
      <svg viewBox="0 0 10 7" className="h-2 w-2.5" aria-hidden="true">
        <path d="M0,0 h10 l-5,7 z" fill={MARKER} />
      </svg>
      Ontik touchpoints
    </li>
  </ul>
);

// Narrows the chart to one working set; with none applied it charts all accounts.
const WorkingSetPicker = ({ sets, applied, onApply }) => {
  const AppliedIcon = applied ? setIcon(applied) : null;
  return (
    <div className="flex items-center gap-1">
      <Menu as="div" className="relative">
        <MenuButton
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium",
            applied
              ? "border-zinc-300 bg-zinc-50 text-zinc-800 hover:bg-zinc-100"
              : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:text-zinc-800",
          )}
        >
          {applied ? (
            <>
              <AppliedIcon aria-hidden="true" className="size-3.5 text-zinc-500" />
              <span className="max-w-40 truncate">{applied.name}</span>
            </>
          ) : (
            "Apply working set"
          )}
          <ChevronDownIcon aria-hidden="true" className="size-4 text-zinc-400" />
        </MenuButton>
        <MenuItems
          anchor="bottom end"
          className="z-30 mt-1 max-h-72 w-60 overflow-y-auto rounded-lg bg-white p-1 text-[13px] shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {sets.map((set) => {
            const Icon = setIcon(set);
            return (
              <MenuItem key={set.id}>
                <button
                  type="button"
                  onClick={() => onApply(set.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-zinc-700 data-[focus]:bg-zinc-100"
                >
                  <Icon aria-hidden="true" className="size-4 shrink-0 text-zinc-400" />
                  <span className="flex-1 truncate">{set.name}</span>
                  {applied?.id === set.id && <CheckIcon aria-hidden="true" className="size-4 text-zinc-700" />}
                </button>
              </MenuItem>
            );
          })}
          {applied && (
            <MenuItem>
              <button
                type="button"
                onClick={() => onApply(null)}
                className="mt-1 flex w-full items-center gap-2 rounded-md border-t border-zinc-100 px-2 py-1.5 text-left text-zinc-500 data-[focus]:bg-zinc-100"
              >
                Show all accounts
              </button>
            </MenuItem>
          )}
        </MenuItems>
      </Menu>
      {applied && (
        <button
          type="button"
          onClick={() => onApply(null)}
          aria-label="Clear working set"
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
        >
          <XMarkIcon aria-hidden="true" className="size-3.5" />
        </button>
      )}
    </div>
  );
};

// Axis labels at weekly steps plus today and month end, dropping any that would crowd today's label.
const axisTicks = (rows, todayDay) => {
  const last = rows.length;
  const candidates = [1, 7, 14, 21, 28, last].filter((d) => d <= last && Math.abs(d - todayDay) > 2);
  return [...new Set([...candidates, todayDay])].sort((a, b) => a - b);
};

const OverdueBurndownCard = ({ workingSets }) => {
  const today = useMemo(() => new Date(), []);
  const [setId, setSetId] = useState(null);
  const applied = workingSets.find((s) => s.id === setId) ?? null;
  const { data, isLoading, isError } = useOverdueBurndown(applied?.filters ?? [], today);

  const month = burndownMonthName(today);
  const shortMonth = month.slice(0, 3);
  const layers = useMemo(() => burndownLayers(today).map((layer, index) => ({ ...layer, index })), [today]);
  const { rows, latest, change } = useMemo(() => buildBurndown(data?.days, today), [data, today]);
  const todayDay = today.getDate();
  const chartRows = rows.map((r) => ({ ...r, placeholder: r.future && latest ? latest : null }));
  const hasData = latest !== null;
  // Debt due this month is only overdue once its day passes, so its layer is often empty early on.
  const legendLayers = layers.filter((layer) => layer.index > 0 || rows.some((r) => r[layer.key] > 0)).reverse();

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Overdue burndown</h3>
        <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
          {data && (
            <span className="inline-flex items-center gap-1">
              <UsersIcon aria-hidden="true" className="size-3.5" />
              {data.accounts} account{data.accounts === 1 ? "" : "s"} · {month}
            </span>
          )}
          {workingSets.length > 0 && <WorkingSetPicker sets={workingSets} applied={applied} onApply={setSetId} />}
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-2xl border border-zinc-200 bg-white p-6 sm:p-8">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-semibold text-zinc-900">
              {isLoading ? <span className="text-zinc-300">—</span> : hasData ? formatAmount(latest) : "—"}
            </span>
            {change !== null && change !== 0 && (
              <span className={cn("text-sm font-medium", change < 0 ? "text-green-700" : "text-red-700")}>
                {change < 0 ? "▼" : "▲"} {formatAmount(Math.abs(change))} vs last month
              </span>
            )}
          </div>
          <Legend layers={legendLayers} />
        </div>

        {isError ? (
          <p className="py-16 text-center text-sm text-zinc-400">Couldn’t load the burndown. Try again shortly.</p>
        ) : !isLoading && !hasData ? (
          <p className="py-16 text-center text-sm text-zinc-400">No overdue history for these accounts yet.</p>
        ) : (
          <div className="h-64 w-full" aria-label={`Overdue balance per day in ${month}`} role="img">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartRows} margin={{ top: 14, right: 4, bottom: 0, left: 0 }} barCategoryGap={3}>
                <XAxis
                  dataKey="day"
                  ticks={axisTicks(rows, todayDay)}
                  interval={0}
                  tickLine={false}
                  axisLine={false}
                  tick={({ x, y, payload }) => (
                    <text x={x} y={y + 12} textAnchor="middle" fontSize={12}
                      fill={payload.value === todayDay ? "#18181b" : "#71717a"}
                      fontWeight={payload.value === todayDay ? 600 : 400}>
                      {payload.value === todayDay ? "Today" : `${payload.value} ${shortMonth}`}
                    </text>
                  )}
                />
                <YAxis tickFormatter={compactAmount} tickLine={false} axisLine={false} width={48}
                  tick={{ fontSize: 12, fill: "#71717a" }} />
                <Tooltip
                  cursor={{ fill: "#f4f4f5", opacity: 0.6 }}
                  content={<BurndownTooltip layers={layers} today={today} />}
                />
                {rows.filter((r) => r.touchpoints > 0).map((r) => (
                  <ReferenceLine key={r.day} x={r.day} stroke={MARKER} strokeDasharray="3 3" strokeOpacity={0.6}
                    label={<TouchpointMarker />} ifOverflow="visible" />
                ))}
                {Array.from({ length: LAYER_COUNT }, (_, i) => (
                  <Bar key={`m${i}`} dataKey={`m${i}`} stackId="overdue" isAnimationActive={false}
                    stroke="#ffffff" strokeWidth={1}>
                    {chartRows.map((r) => (
                      <Cell key={r.day} fill={(r.touchpoints > 0 ? VIOLET : GRAY)[i]} />
                    ))}
                  </Bar>
                ))}
                <Bar dataKey="placeholder" stackId="overdue" shape={<PlaceholderBar />} isAnimationActive={false} />
                <Line dataKey="lastMonth" type="monotone" stroke={LAST_MONTH} strokeWidth={1.5} dot={false}
                  activeDot={false} connectNulls isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
};

export default OverdueBurndownCard;
